import { LitElement, html, css } from 'lit-all'
import { t, refreshFocusTrap } from 'lib-utils'
class NovaSearchForm extends LitElement {
  static properties = {
    url: { type: String },
    value: { type: String },
    placeholder: { type: String },
    label: { type: String },
    noResults: { type: String },
    searchAgain: { type: String },
    openState: { type: String },
  }

  constructor() {
    super()
    this.searchData = {}
    this.searchTerm = ''
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties)
    const searchInput = this.shadowRoot.querySelector('.header-search__input')
    searchInput.addEventListener('input', this.searchOnChange.bind(this))
    if (this.value) {
      this.getSearchResults(this.value)
    }
  }

  searchOnChange(event) {
    if (this.openState === 'closed') return

    const searchValue = event.target.value
    this.value = searchValue
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      this.getSearchResults(searchValue)
    }, 300)
  }

  async getSearchResults(searchTerm) {
    if (!searchTerm.length) {
      this.searchData = {}
      this.searchTerm = ''
      refreshFocusTrap(this, 0)
      this.requestUpdate()
      return
    }

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(
          searchTerm
        )}&resources[type]=query,product,collection,page,article`
      )
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        this.searchData = {}
        this.searchTerm = ''
      }
      const data = await response.json()
      this.searchData = data
      this.searchTerm = searchTerm
    } catch (error) {
      console.error('Error:', error.message)
      this.searchData = {}
      this.searchTerm = ''
    }
    this.requestUpdate()
  }

  render() {
    return html`
      <form
        class="header-search__form"
        action="${this.url}"
        method="get"
        role="search"
        aria-label="${this.label}"
      >
        <div class="field header-search__field">
          <slot name="search-icon"></slot>
          <input
            class="header-search__input h4"
            placeholder="Search entire store here..."
            id="Search-In-Modal"
            type="search"
            name="q"
            value="${this.value}"
            autofocus
            aria-label="${this.label}"
            aria-autocomplete="list"
            aria-controls="search-results"
          />
          <input type="hidden" name="options[prefix]" value="last" />
          <button class="visually-hidden" tabindex="-1">
            ${t('general.search.search')}
          </button>
          <slot name="close-button"></slot>
        </div>
        <label class="header-search__label" for="Search-In-Modal"
          >Press 'Enter' to search</label
        >
      </form>
      <nova-search-result
        .searchData=${this.searchData}
        .searchTerm=${this.searchTerm}
        openState=${this.openState}
        noResults=${t('general.search.no_results_found_for')}
        searchAgain=${t('general.search.try_again')}
        role="listbox"
        aria-expanded="${Object.keys(this.searchData).length !== 0}"
      ></nova-search-result>
    `
  }

  static styles = css`
    :host {
      width: 100%;
    }

    :host .header-search__form {
      width: 100%;
    }

    :host .header-search__field {
      display: flex;
      align-items: center;
      border-bottom: 1px solid var(--nova-brand-primary);
    }

    :host .header-search__input {
      width: 100%;
      border: none;
      font: var(--nova-h6-font);
      font-size: 24px;
      background-color: var(--nova-color-background);
    }

    :host .visually-hidden {
      position: absolute !important;
      overflow: hidden;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      word-wrap: normal !important;
    }

    :host .header-search__label {
      display: block;
      padding-top: var(--nova-space-xs);
      color: var(--nova-brand-subdued);
      font-size: 16px;
    }

    :host .header-search__input:focus-visible {
      outline: none;
    }

    /* Clear default clear button from Internet Explorer */
    :host input[type='search']::-ms-clear {
      display: none;
      -webkit-appearance: none;
      width: 0;
      height: 0;
    }

    /* Clear default clear button from Chrome */
    :host input[type='search']::-webkit-search-cancel-button {
      display: none;
      -webkit-appearance: none;
    }

    ::slotted([slot='search-icon']) {
      padding-right: var(--nova-space-xs);
    }

    @media screen and (min-width: 750px) {
      ::slotted([slot='search-icon']) {
        display: none;
      }
    }
  `
}

customElements.define('nova-search-form', NovaSearchForm)
